// ── Ore adapter ─────────────────────────────────────────────────────────────
// Utilities that bridge Ore's component lifecycle with Refine's core
// primitives' `AbortSignal`-based lifecycle contract.

/**
 * Creates an `AbortSignal` tied to the component's Ore lifecycle.
 *
 * Call once at the top of a `setup()` function and pass the resulting signal
 * as the required `signal` option to any stateful core primitive
 * (`createTextField`, `createCheckable`, `createListboxDropdown`, etc.).
 *
 * When Ore disconnects the component, `onCleanup` fires, aborting the
 * signal and triggering cleanup in every primitive that received it.
 *
 * @example
 * ```ts
 * setup(props, { el }) {
 *   const signal = lifecycleSignal(onCleanup);
 *   const tf = createTextField({ ..., signal });
 * }
 * ```
 */
export const lifecycleSignal = (onCleanup: (fn: () => void) => void): AbortSignal => {
  const controller = new AbortController();

  onCleanup(() => controller.abort());

  return controller.signal;
};
